import React, { useState } from "react";
import { X, AlertCircle, CheckCircle, Upload } from "lucide-react";

interface RepomixImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (xmlContent: string) => Promise<{ error: string | null }>;
}

const RepomixImportModal: React.FC<RepomixImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
    }
  };

  const handleImportClick = async () => {
    if (!file) {
      setError("Please select a file to import");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Read the file content
      const content = await readFileContent(file);
      
      // Call the import handler passed from the parent
      const result = await onImport(content);
      
      setIsLoading(false);
      
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
        // Close modal after a short delay on success
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setIsLoading(false);
      setError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === 'string') {
          resolve(event.target.result);
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };

  const handleClose = () => {
    // Reset state when closing
    setFile(null);
    setError(null);
    setIsLoading(false);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Import from Repomix XML</h3>
          <button onClick={handleClose} className="hover:bg-muted rounded p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <div className="p-4 flex-1 overflow-auto">
          <p className="text-sm text-muted-foreground mb-4">
            Upload your Repomix XML file to visualize your codebase structure and dependencies.
            You'll be able to filter and explore the diagram using natural language queries.
          </p>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="repomix-file"
              accept=".xml"
              onChange={handleFileChange}
              className="hidden"
              disabled={isLoading || success}
            />
            <label 
              htmlFor="repomix-file"
              className="cursor-pointer flex flex-col items-center justify-center"
            >
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm font-medium">
                {file ? file.name : 'Click to select Repomix XML file'}
              </span>
              <span className="text-xs text-gray-500 mt-1">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : '.xml files only'}
              </span>
            </label>
          </div>
          
          {error && (
            <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>Import successful! Closing modal...</span>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t flex justify-end gap-2">
          <button 
            onClick={handleClose}
            className="px-3 py-2 rounded text-sm bg-muted/50 hover:bg-muted text-foreground"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            onClick={handleImportClick}
            disabled={!file || isLoading || success}
            className="px-3 py-2 rounded text-sm bg-primary/90 hover:bg-primary text-white disabled:opacity-50 disabled:pointer-events-none"
          >
            {isLoading
              ? "Importing..."
              : success
              ? "Success!"
              : "Import Diagram"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepomixImportModal;